import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    Paper,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Fragment, type JSX } from 'react';

import { useLocale } from '@repo/i18n';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';
import TypographyStyleUtils from 'src/components/utils/jss/TypographyStyleUtils';
import { capitalize } from '@repo/common-utils/TextUtils';
import { validateEmail, validateTextField } from '@repo/common-utils/validation-helper';

export type RecipientDetails = {
    firstName: string;
    lastName: string;
    email: string;
};

type Props = {
    showErrors: boolean;
    recipient: RecipientDetails;
    onRecipientChange: (recipient: RecipientDetails) => void;
    validate: (isValid: boolean) => void;
};

function validateRecipient(recipient: RecipientDetails) {
    const isFirstNameValid = validateTextField(recipient.firstName);
    const isLastNameValid = validateTextField(recipient.lastName);
    const isEmailValid = validateEmail(recipient.email);
    const isValid = isFirstNameValid && isLastNameValid && isEmailValid;
    return {
        isFirstNameValid,
        isLastNameValid,
        isEmailValid,
        isValid,
    };
}
export default function GiftcardRecipientDetails(props: Props): JSX.Element {
    const { t } = useLocale();
    const largeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
    const { isFirstNameValid, isLastNameValid, isEmailValid, isValid } = validateRecipient(
        props.recipient,
    );
    const theme = useTheme();
    const showFirstNameValidationError = !isFirstNameValid && props.showErrors;
    const showLastNameValidationError = !isLastNameValid && props.showErrors;
    const showEmailValidationError = !isEmailValid && props.showErrors;
    props.validate(isValid);

    return (
        <Paper
            sx={{
                display: 'flex',
                flexDirection: 'column',
                '& > *': {
                    marginTop: theme.spacing(1),
                },
                marginBottom: theme.spacing(2),
                backgroundColor: theme.palette.common.white,
                width: '100%',
                marginTop: theme.spacing(2),
                border: `1px solid ${theme.palette.grey[300]}`,
            }}
            variant="outlined"
        >
            <Typography variant="h6" mt={0} sx={TypographyStyleUtils.secondaryTextColorSx}>
                {capitalize(t.recipient_details)}
            </Typography>

            <Fragment>
                <FormControl
                    fullWidth
                    sx={{
                        '& > *': {
                            marginTop: theme.spacing(1),
                        },
                        backgroundColor: theme.palette.common.white,
                    }}
                >
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        wrap={largeScreen ? 'nowrap' : 'wrap'}
                    >
                        <Box
                            flexBasis={largeScreen ? '50%' : '100%'}
                            width="100%"
                            mr={largeScreen ? theme.spacing(1) : 0}
                            sx={{
                                [theme.breakpoints.down('md')]: {
                                    minWidth: '300px',
                                },
                            }}
                        >
                            <InputLabel
                                sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                                required
                                error={showFirstNameValidationError}
                            >
                                {capitalize(t.first_name)}
                            </InputLabel>
                            <TextField
                                fullWidth
                                sx={{
                                    marginRight: largeScreen ? theme.spacing(1) : 0,
                                    [theme.breakpoints.down('md')]: {
                                        minWidth: '300px',
                                    },
                                }}
                                error={showFirstNameValidationError}
                                variant="outlined"
                                type="text"
                                color="secondary"
                                margin="dense"
                                size="small"
                                spellCheck={false}
                                onChange={(e) =>
                                    props.onRecipientChange({
                                        ...props.recipient,
                                        firstName: e.currentTarget.value,
                                    })
                                }
                                value={props.recipient.firstName}
                            />
                        </Box>
                        <Box
                            flexBasis={largeScreen ? '50%' : '100%'}
                            width="100%"
                            ml={largeScreen ? theme.spacing(1) : 0}
                            sx={{
                                [theme.breakpoints.down('md')]: {
                                    minWidth: '300px',
                                },
                            }}
                        >
                            <InputLabel
                                sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                                required
                                error={showLastNameValidationError}
                            >
                                {capitalize(t.last_name)}
                            </InputLabel>
                            <TextField
                                fullWidth
                                sx={{
                                    marginRight: largeScreen ? theme.spacing(1) : 0,
                                    [theme.breakpoints.down('md')]: {
                                        minWidth: '300px',
                                    },
                                }}
                                error={showLastNameValidationError}
                                variant="outlined"
                                type="text"
                                color="secondary"
                                margin="dense"
                                size="small"
                                spellCheck={false}
                                onChange={(e) =>
                                    props.onRecipientChange({
                                        ...props.recipient,
                                        lastName: e.currentTarget.value,
                                    })
                                }
                                value={props.recipient.lastName}
                            />
                        </Box>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        wrap={largeScreen ? 'nowrap' : 'wrap'}
                    >
                        <Box
                            mr={largeScreen ? theme.spacing(1) : 0}
                            width={largeScreen ? '50%' : '100%'}
                            sx={{
                                [theme.breakpoints.down('md')]: {
                                    minWidth: '300px',
                                },
                            }}
                        >
                            <InputLabel
                                sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                                required
                                error={showEmailValidationError}
                            >
                                {capitalize(t.email)}
                            </InputLabel>
                            <TextField
                                fullWidth
                                sx={{
                                    marginRight: largeScreen ? theme.spacing(1) : 0,
                                    [theme.breakpoints.down('md')]: {
                                        minWidth: '300px',
                                    },
                                }}
                                error={showEmailValidationError}
                                variant="outlined"
                                type="text"
                                color="secondary"
                                margin="dense"
                                size="small"
                                // type="text" (not "email"), so the browser would spellcheck the address.
                                spellCheck={false}
                                onChange={(e) =>
                                    props.onRecipientChange({
                                        ...props.recipient,
                                        email: e.currentTarget.value,
                                    })
                                }
                                value={props.recipient.email}
                            />
                        </Box>
                        <Box
                            ml={largeScreen ? theme.spacing(1) : 0}
                            flexBasis={largeScreen ? '50%' : '100%'}
                            width="100%"
                            sx={{
                                [theme.breakpoints.down('md')]: {
                                    minWidth: '300px',
                                },
                            }}
                        ></Box>
                    </Grid>
                </FormControl>
            </Fragment>
        </Paper>
    );
}
